import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../services/api';
import Layout from '../components/Layout';
import { FaUniversity, FaClipboardList, FaUserShield, FaUser, FaChartPie, FaCompass } from 'react-icons/fa';
import ProfileTab from '../components/ProfileTab';
import ReportsTab from '../components/ReportsTab';
import ClubModal from '../components/modals/ClubModal';
import AdminModal from '../components/modals/AdminModal';
import ProjectModal from '../components/modals/ProjectModal';
import ClubsTable from '../components/tables/ClubsTable';
import ProjectsTable from '../components/tables/ProjectsTable';
import AdminsTable from '../components/tables/AdminsTable';
import ConfirmationModal from '../components/ConfirmationModal';
import DiscoverTab from '../components/dashboard/DiscoverTab';

const DistrictAdminDashboard = () => {
    const [user, setUser] = useState(null);
    const [activeTab, setActiveTab] = useState('clubs');
    const [clubs, setClubs] = useState([]);
    const [projects, setProjects] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('');
    const [formData, setFormData] = useState({});
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: null, id: null });
    const navigate = useNavigate();

    const tabs = [
        { id: 'clubs', label: 'Manage Clubs', icon: <FaUniversity /> },
        { id: 'projects', label: 'Manage Projects', icon: <FaClipboardList /> },
        { id: 'discover', label: 'Discover', icon: <FaCompass /> },
        { id: 'admins', label: 'Manage Admins', icon: <FaUserShield /> },
        { id: 'reports', label: 'Reports', icon: <FaChartPie /> },
        { id: 'profile', label: 'My Profile', icon: <FaUser /> },
    ];

    const fetchClubs = useCallback(async () => {
        setLoading(true);
        const data = await adminApi.getClubs();
        if (data.success) setClubs(data.data.clubs);
        setLoading(false);
    }, []);

    const fetchProjects = useCallback(async () => {
        const data = await adminApi.getProjects();
        if (data.success) setProjects(data.data.projects);
    }, []);

    const fetchAdmins = useCallback(async () => {
        const data = await adminApi.getAdmins();
        if (data.success) setAdmins(data.data.admins);
    }, []);

    const fetchData = useCallback(() => {
        fetchClubs();
        fetchProjects();
        fetchAdmins();
    }, [fetchClubs, fetchProjects, fetchAdmins]);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
            navigate('/login');
            return;
        }
        const userData = JSON.parse(storedUser);
        if (userData.role !== 'district_admin') {
            navigate('/login');
            return;
        }
        setUser(userData);
        fetchData();
    }, [navigate, fetchData]);

    const openModal = (type, data = {}) => {
        setModalType(type);
        setFormData(data);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setFormData({});
        setModalType('');
    };

    const handleModalSubmit = async (data) => {
        setLoading(true);
        try {
            let res;
            if (modalType === 'create-club' || modalType === 'edit-club') {
                const clubData = { ...data, sub_district_id: user.sub_district_id };
                res = modalType === 'edit-club' ? await adminApi.updateClub(clubData) : await adminApi.createClub(clubData);
            } else if (modalType === 'create-admin' || modalType === 'edit-admin') {
                res = data.id ? await adminApi.updateAdmin(data) : await adminApi.createAdmin(data);
            } else if (modalType === 'edit-project') {
                res = await adminApi.updateProject(data);
            }

            if (res.success) {
                closeModal();
                fetchData();
                toast.success('Success!');
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            toast.error('Error: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (type, id) => {
        setConfirmModal({
            isOpen: true,
            type,
            id,
            title: `Delete ${type === 'club' ? 'Club' : type === 'project' ? 'Project' : 'Admin'}`,
            message: `Are you sure you want to delete this ${type}? This action cannot be undone.`
        });
    };

    const executeDelete = async () => {
        const { type, id } = confirmModal;
        try {
            let res;
            if (type === 'club') res = await adminApi.deleteClub(id);
            else if (type === 'project') res = await adminApi.deleteProject(id);
            else if (type === 'admin') res = await adminApi.deleteAdmin(id);

            if (res.success) {
                fetchData();
                toast.success(`${type} deleted successfully`);
                setConfirmModal({ isOpen: false, type: null, id: null });
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            toast.error('Error: ' + error.message);
        }
    };

    const renderContent = () => {
        if (loading && !clubs.length && !projects.length && !admins.length) return <div className="p-8 text-center text-gray-500">Loading...</div>;

        switch (activeTab) {
            case 'clubs':
                return (
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">My Clubs</h2>
                            <button
                                onClick={() => openModal('create-club')}
                                className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                            >
                                <span className="text-lg">+</span> Add Club
                            </button>
                        </div>
                        <ClubsTable
                            clubs={clubs}
                            onEdit={(club) => openModal('edit-club', club)}
                            onDelete={(id) => handleDeleteClick('club', id)}
                        />
                    </div>
                );

            case 'projects':
                return (
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">Projects</h2>
                        </div>
                        <ProjectsTable
                            projects={projects}
                            onEdit={(project) => openModal('edit-project', project)}
                            onDelete={(id) => handleDeleteClick('project', id)}
                        />
                    </div>
                );

            case 'admins':
                return (
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">Club Admins</h2>
                            <button
                                onClick={() => openModal('create-admin', { role: 'club_admin' })}
                                className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                            >
                                <span className="text-lg">+</span> Add Club Admin
                            </button>
                        </div>
                        <AdminsTable
                            admins={admins}
                            onEdit={(admin) => openModal('edit-admin', admin)}
                            onDelete={(id) => handleDeleteClick('admin', id)}
                        />
                    </div>
                );

            case 'reports':
                return <ReportsTab user={user} />;

            case 'discover':
                return <DiscoverTab />;

            case 'profile':
                return <ProfileTab user={user} />;

            default:
                return null;
        }
    };

    return (
        <Layout user={user} activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs}>
            {renderContent()}

            {(modalType === 'create-club' || modalType === 'edit-club') && (
                <ClubModal
                    isOpen={showModal}
                    onClose={closeModal}
                    onSubmit={handleModalSubmit}
                    result={formData}
                    loading={loading}
                />
            )}

            {(modalType === 'create-admin' || modalType === 'edit-admin') && (
                <AdminModal
                    isOpen={showModal}
                    onClose={closeModal}
                    onSubmit={handleModalSubmit}
                    result={formData}
                    loading={loading}
                    clubs={clubs}
                />
            )}

            {(modalType === 'edit-project') && (
                <ProjectModal
                    isOpen={showModal}
                    onClose={closeModal}
                    onSubmit={handleModalSubmit}
                    result={formData}
                    loading={loading}
                    clubs={clubs}
                />
            )}

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                onConfirm={executeDelete}
                title={confirmModal.title}
                message={confirmModal.message}
                loading={false}
            />
        </Layout>
    );
};

export default DistrictAdminDashboard;
